import React, { useEffect, useState } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import CustomerPanel from './components/CustomerPanel';
import MapView from './components/MapView';
import UserView from './components/UserView';
import Header from './components/Header';
import LoginPage from './components/LoginPage';
import MagicLinkVerify from './components/MagicLinkVerify';
import { Customer } from './types/types';
import { calculateOptimizedRoute } from './utils/mapUtils';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { saveCustomers, loadCustomers } from './utils/storageUtils';
// Libraries needed for Google Maps
const libraries = ['places', 'directions'];
const AppContent = () => {
  const [role, setRole] = useState<'user' | 'manager'>('user');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [optimizedRoute, setOptimizedRoute] = useState<any>(null);
  const [routeSummary, setRouteSummary] = useState<{
    distance: string;
    duration: string;
  } | null>(null);
  const [view, setView] = useState<'app' | 'login' | 'verify'>('app');
  const [verifyToken, setVerifyToken] = useState<string>('');
  const {
    isAuthenticated
  } = useAuth();
  // Load Google Maps script
  const {
    isLoaded,
    loadError
  } = useLoadScript({
    googleMapsApiKey: 'AIzaSyD-B_Rlps2YJxoWjQ77WRaQGbB1TdYgT18',
    libraries: libraries as any
  });
  // Load customers from localStorage on initial render
  useEffect(() => {
    const savedCustomers = loadCustomers();
    if (savedCustomers.length > 0) {
      setCustomers(savedCustomers);
    } else {
      // Set default customer if no saved customers
      setCustomers([{
        id: '1',
        name: 'Route Planning',
        address: 'Via Giacomo Boni, 45-37, 20146 Milano MI, Italia',
        location: {
          lat: 45.4590833,
          lng: 9.1539442
        },
        availableDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        timeOnSite: 60,
        selected: true
      }]);
    }
  }, []);
  // Check for magic link token in URL on component mount
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const token = queryParams.get('token');
    if (token) {
      setVerifyToken(token);
      setView('verify');
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (!isAuthenticated && role === 'manager') {
      setView('login');
    } else {
      setView('app');
    }
  }, [isAuthenticated, role]);
  const addCustomer = (customer: Customer) => {
    const newCustomers = [...customers, {
      ...customer,
      id: Date.now().toString(),
      selected: true
    }];
    setCustomers(newCustomers);
    // Save to localStorage
    saveCustomers(newCustomers);
  };
  const toggleCustomerSelection = (id: string) => {
    const updatedCustomers = customers.map(customer => customer.id === id ? {
      ...customer,
      selected: !customer.selected
    } : customer);
    setCustomers(updatedCustomers);
    // Save to localStorage
    saveCustomers(updatedCustomers);
  };
  const optimizeRoute = () => {
    const selectedCustomers = customers.filter(customer => customer.selected);
    if (selectedCustomers.length < 2) {
      alert('Please select at least 2 customers to optimize a route.');
      return;
    }
    // Get current day of week
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const currentDay = days[new Date().getDay()];
    // Filter customers by availability on current day
    const availableCustomers = selectedCustomers.filter(customer => customer.availableDays.includes(currentDay));
    if (availableCustomers.length < 2) {
      alert(`Not enough customers available on ${currentDay}. Please select more customers or try another day.`);
      return;
    }
    // Calculate optimized route
    calculateOptimizedRoute(availableCustomers).then(result => {
      setOptimizedRoute(result.route);
      setRouteSummary(result.summary);
    }).catch(error => {
      console.error('Error optimizing route:', error);
      alert('Error optimizing route. Please try again.');
    });
  };
  const handleSendMagicLink = (email: string, token: string) => {
    console.log(`Magic link for ${email} with token ${token} would be sent in a real app`);
    // In a real app, we would send an email here
  };
  const handleVerifySuccess = () => {
    setView('app');
  };
  if (view === 'login') {
    return <LoginPage onSendMagicLink={handleSendMagicLink} data-id="element-0" />;
  }
  if (view === 'verify') {
    return <MagicLinkVerify token={verifyToken} onSuccess={handleVerifySuccess} data-id="element-1" />;
  }
  if (loadError) {
    return <div className="w-full h-screen flex items-center justify-center" data-id="element-2">
        Error loading Google Maps
      </div>;
  }
  if (!isLoaded) {
    return <div className="w-full h-screen flex items-center justify-center" data-id="element-3">
        Loading Google Maps...
      </div>;
  }
  return <div className="flex flex-col h-screen w-full bg-gray-100" data-id="element-4">
      <Header currentRole={role} onRoleChange={setRole} data-id="element-5" />
      {role === 'manager' && !isAuthenticated ? <div className="flex-1 flex items-center justify-center" data-id="element-6">
          <div className="text-center p-8 bg-white rounded-lg shadow-md max-w-md" data-id="element-7">
            <h2 className="text-xl font-semibold mb-4" data-id="element-8">
              Map Manager Access Required
            </h2>
            <p className="mb-6 text-gray-600" data-id="element-9">
              You need to log in to access the Map Manager features.
            </p>
            <button onClick={() => setView('login')} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700" data-id="element-10">
              Log In
            </button>
          </div>
        </div> : role === 'manager' ? <div className="flex flex-col md:flex-row flex-1" data-id="element-11">
          <CustomerPanel customers={customers} addCustomer={addCustomer} toggleCustomerSelection={toggleCustomerSelection} optimizeRoute={optimizeRoute} routeSummary={routeSummary} data-id="element-12" />
          <MapView customers={customers} optimizedRoute={optimizedRoute} data-id="element-13" />
        </div> : <UserView customers={customers} optimizedRoute={optimizedRoute} routeSummary={routeSummary} optimizeRoute={optimizeRoute} data-id="element-14" />}
    </div>;
};
export function App() {
  return <AuthProvider data-id="element-15">
      <AppContent data-id="element-16" />
    </AuthProvider>;
}